/**
 * Read-only Investigation and Closure display for ViewIncident.
 * Uses the same design patterns as ViewIncident: Field, InfoCard, Badge.
 * Manager/SP Admin only - Blue portal excluded.
 */
import moment from "moment";
import { classNames } from "Utils/utils";
import {
  INVESTIGATION_CHECKS,
  REMEDIAL_ACTIONS,
} from "../constants";
import {
  formatYesNo,
  getOptionLabel as getLabel,
} from "../utils/formatters";
import useCareLabels from "hooks/useCareLabels";

// Reusable field display - matches ViewIncident.Field
const Field = ({ label, value, className = "" }) => (
  <div className={className}>
    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">
      {label}
    </label>
    <p className="text-sm text-gray-800 mt-0.5">{value || "-"}</p>
  </div>
);

// Reusable info card - matches ViewIncident.InfoCard
const InfoCard = ({ label, value, variant = "default" }) => {
  const variants = {
    default: "bg-gray-50 text-gray-700",
    success: "bg-green-50 text-green-700",
    danger: "bg-red-50 text-red-700",
    warning: "bg-amber-50 text-amber-700",
    info: "bg-blue-50 text-blue-700",
  };
  return (
    <div
      className={classNames(
        "px-3 py-2 rounded-lg text-center border",
        variants[variant],
      )}
    >
      <p className="text-xs text-gray-500 mb-0.5">{label}</p>
      <p className="text-sm font-semibold">{value || "-"}</p>
    </div>
  );
};

/**
 * Read-only Investigation display (matches ViewIncident design)
 */
export const InvestigationViewReadOnly = ({ investigation }) => {
  const { participantLabel } = useCareLabels();
  const data = investigation?.data;
  if (!data) return null;

  const participantNotes = data?.participant_interviews || [];
  const staffNotes = data?.staff_interviews || [];
  const remedialActions = data?.remedial_actions || [];

  return (
    <div className="space-y-6">
      {/* Is Reportable */}
      <div className="grid grid-cols-2 gap-4">
        <InfoCard
          label="Is the incident reportable?"
          value={data?.is_reportable === "yes" ? "Yes" : data?.is_reportable === "no" ? "No" : "-"}
          variant={
            data?.is_reportable === "yes"
              ? "danger"
              : data?.is_reportable === "no"
                ? "success"
                : "default"
          }
        />
        {investigation?.started_at && (
          <Field
            label="Investigation started"
            value={moment(investigation.started_at).format("DD/MM/YYYY hh:mm A")}
          />
        )}
      </div>

      {/* Interview Notes - Two-column grid, scroll after 5 items (max 10 each) */}
      {(participantNotes.some((n) => n?.notes?.trim()) ||
        staffNotes.some((n) => n?.notes?.trim())) && (
        <div className="pt-4 border-t border-gray-100">
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
            Interview Notes
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {participantNotes.filter((n) => n?.notes?.trim()).length > 0 && (
              <div>
                <p className="text-xs text-gray-500 mb-2 font-medium">
                  {participantLabel} ({participantNotes.filter((n) => n?.notes?.trim()).length}/10)
                </p>
                <div
                  className="space-y-2 pr-4 max-h-96 overflow-y-auto"
                  style={{
                    scrollbarColor: "#38528A transparent",
                    scrollbarWidth: "thin",
                  }}
                >
                  {participantNotes
                    .filter((n) => n?.notes?.trim())
                    .map((n, i) => (
                      <div
                        key={i}
                        className="bg-gray-50 p-3 rounded-lg text-sm text-gray-800 incident-view-scroll max-h-24 overflow-y-auto"
                        style={{
                          scrollbarColor: "#38528A transparent",
                          scrollbarWidth: "thin",
                        }}
                      >
                        <span className="text-xs text-gray-500 font-medium">#{i + 1}</span>
                        <p className="mt-0.5 whitespace-pre-wrap">{n.notes}</p>
                      </div>
                    ))}
                </div>
              </div>
            )}
            {staffNotes.filter((n) => n?.notes?.trim()).length > 0 && (
              <div>
                <p className="text-xs text-gray-500 mb-2 font-medium">
                  Staff ({staffNotes.filter((n) => n?.notes?.trim()).length}/10)
                </p>
                <div
                  className="space-y-2 pr-4 max-h-96 overflow-y-auto"
                  style={{
                    scrollbarColor: "#38528A transparent",
                    scrollbarWidth: "thin",
                  }}
                >
                  {staffNotes
                    .filter((n) => n?.notes?.trim())
                    .map((n, i) => (
                      <div
                        key={i}
                        className="bg-gray-50 p-3 rounded-lg text-sm text-gray-800 incident-view-scroll max-h-24 overflow-y-auto"
                        style={{
                          scrollbarColor: "#38528A transparent",
                          scrollbarWidth: "thin",
                        }}
                      >
                        <span className="text-xs text-gray-500 font-medium">#{i + 1}</span>
                        <p className="mt-0.5 whitespace-pre-wrap">{n.notes}</p>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Advocacy */}
      {(data?.advocacy_offered || data?.advocacy_contact_name || data?.advocacy_details) && (
        <div className="pt-4 border-t border-gray-100">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InfoCard
              label="Advocacy offered?"
              value={formatYesNo(data.advocacy_offered)}
              variant={data.advocacy_offered === "yes" ? "success" : "default"}
            />
            {data.advocacy_offered === "yes" && data?.advocacy_contact_name && (
              <Field label="Details" value={data.advocacy_contact_name} />
            )}
            {data.advocacy_offered === "no" && data?.advocacy_details && (
              <Field label="Details" value={data.advocacy_details} />
            )}
          </div>
        </div>
      )}

      {/* Investigation Checks */}
      {INVESTIGATION_CHECKS.some((c) => data[c.key]) && (
        <div className="pt-4 border-t border-gray-100">
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
            Investigation Checks
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {INVESTIGATION_CHECKS.filter((c) => data[c.key]).map((c) => (
              <div
                key={c.key}
                className="flex justify-between items-center py-2 px-3 bg-gray-50 rounded-lg border border-gray-100"
              >
                <span className="text-sm text-gray-700 pr-2">{c.label}</span>
                <span
                  className={classNames(
                    "text-xs font-medium px-2 py-0.5 rounded",
                    (data[c.key] === "yes" || data[c.key] === "Yes") &&
                      "bg-green-100 text-green-700",
                    (data[c.key] === "no" || data[c.key] === "No") &&
                      "bg-red-100 text-red-700",
                    (data[c.key] === "na" ||
                      data[c.key] === "NA" ||
                      data[c.key] === "not_applicable") &&
                      "bg-gray-200 text-gray-700",
                  )}
                >
                  {formatYesNo(data[c.key])}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Root Cause - scrollable for long text */}
      {data?.root_cause_analysis && (
        <div className="pt-4 border-t border-gray-100">
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
            Root cause analysis
          </label>
          <div
            className="incident-view-scroll max-h-40 overflow-y-auto pr-4"
            style={{
              scrollbarColor: "#38528A transparent",
              scrollbarWidth: "thin",
            }}
          >
            <p className="text-sm text-gray-800 whitespace-pre-wrap">
              {data.root_cause_analysis}
            </p>
          </div>
        </div>
      )}

      {/* Remedial Actions */}
      {(remedialActions.length > 0 || data?.remedial_actions_other) && (
        <div className="pt-4 border-t border-gray-100">
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
            Remedial Actions Recommended
          </label>
          {remedialActions.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-2">
              {remedialActions.map((val) => {
                const action = REMEDIAL_ACTIONS.find((a) => a.value === val);
                return (
                  <span
                    key={val}
                    className="inline-flex items-center px-2.5 py-1 rounded-md border border-gray-200 bg-blue-50 text-blue-700 text-xs font-medium"
                  >
                    {action?.label || val}
                  </span>
                );
              })}
            </div>
          )}
          {data?.remedial_actions_other && (
            <div
              className="mt-2 text-sm text-gray-800 max-h-24 overflow-y-auto pr-4 incident-view-scroll"
              style={{
                scrollbarColor: "#38528A transparent",
                scrollbarWidth: "thin",
              }}
            >
              <p className="whitespace-pre-wrap">Other: {data.remedial_actions_other}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Read-only Closure/Review display (matches ViewIncident design)
 */
export const ClosureViewReadOnly = ({ closure, ndisReferenceNumber }) => {
  const data = closure?.data;
  if (!data) return null;

  return (
    <div className="space-y-6">
      {/* Summary row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {closure?.closed_at && (
          <Field
            label="Closed at"
            value={moment(closure.closed_at).format("DD/MM/YYYY hh:mm A")}
          />
        )}
        <InfoCard
          label="New risks identified?"
          value={formatYesNo(data?.new_risks_identified)}
          variant={data?.new_risks_identified === "yes" ? "warning" : "default"}
        />
        <InfoCard
          label="Risk prevention required?"
          value={formatYesNo(data?.risk_prevention_required)}
          variant={
            data?.risk_prevention_required === "yes" ? "info" : "default"
          }
        />
        <InfoCard
          label="Final status"
          value={data?.final_status === "reported" ? "Reported to NDIS" : "Closed"}
          variant={
            data?.final_status === "reported" ? "info" : "success"
          }
        />
      </div>

      {/* Risk recurrence */}
      {data?.risk_recurrence_rating && (
        <div className="pt-4 border-t border-gray-100">
          <InfoCard
            label="Risk recurrence rating"
            value={getLabel(
              [
                { value: "nil", label: "Nil" },
                { value: "low", label: "Low" },
                { value: "medium", label: "Medium" },
                { value: "high", label: "High" },
              ],
              data.risk_recurrence_rating,
            )}
            variant={
              data.risk_recurrence_rating === "high"
                ? "danger"
                : data.risk_recurrence_rating === "medium"
                  ? "warning"
                  : "default"
            }
          />
        </div>
      )}

      {/* Conditional text fields - scrollable for long text */}
      {data?.new_risks_details && (
        <div className="pt-4 border-t border-gray-100">
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
            New risks details
          </label>
          <div
            className="incident-view-scroll max-h-40 overflow-y-auto pr-4"
            style={{
              scrollbarColor: "#38528A transparent",
              scrollbarWidth: "thin",
            }}
          >
            <p className="text-sm text-gray-800 whitespace-pre-wrap">
              {data.new_risks_details}
            </p>
          </div>
        </div>
      )}

      {data?.risk_prevention_details && (
        <div className="pt-4 border-t border-gray-100">
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
            Risk prevention / mitigation
          </label>
          <div
            className="incident-view-scroll max-h-40 overflow-y-auto pr-4"
            style={{
              scrollbarColor: "#38528A transparent",
              scrollbarWidth: "thin",
            }}
          >
            <p className="text-sm text-gray-800 whitespace-pre-wrap">
              {data.risk_prevention_details}
            </p>
          </div>
        </div>
      )}

      {data?.further_action_needed && (
        <div className="pt-4 border-t border-gray-100">
          <InfoCard
            label="Further action needed?"
            value={formatYesNo(data.further_action_needed)}
            variant={
              data.further_action_needed === "yes" ? "warning" : "default"
            }
          />
          {data?.further_action_details && (
            <div className="mt-2">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                Actions and timeline
              </label>
              <div
                className="incident-view-scroll max-h-40 overflow-y-auto pr-4"
                style={{
                  scrollbarColor: "#38528A transparent",
                  scrollbarWidth: "thin",
                }}
              >
                <p className="text-sm text-gray-800 whitespace-pre-wrap">
                  {data.further_action_details}
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {data?.manager_review_comments && (
        <div className="pt-4 border-t border-gray-100">
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
            Manager review comments
          </label>
          <div
            className="incident-view-scroll max-h-40 overflow-y-auto pr-4"
            style={{
              scrollbarColor: "#38528A transparent",
              scrollbarWidth: "thin",
            }}
          >
            <p className="text-sm text-gray-800 whitespace-pre-wrap">
              {data.manager_review_comments}
            </p>
          </div>
        </div>
      )}
      {closure?.reported_to_commission_at && (
        <div className="pt-4 border-t border-gray-100">
          <Field
            label="Reported to NDIS Commission"
            value={moment(closure.reported_to_commission_at).format("DD/MM/YYYY")}
          />
        </div>
      )}

{ndisReferenceNumber && (
      <div className="pt-4 border-t border-gray-100">
        <Field
          label="NDIS Commission Reference Number"
          value={ndisReferenceNumber || "-"}
        />
      </div>)}
    </div>
  );
};
